/**
 * Profile Cloud Function service.
 * Wraps all httpsCallable calls for user profiles, badges, and online status.
 * Profile reads use the public Cloud Function (no auth required for basic data).
 * Profile view logging requires authentication.
 */

import { httpsCallable } from 'firebase/functions';
import { FIREBASE_FUNCTIONS } from '../firebaseConfig';
import type {
  GetPublicProfileResponse,
  GetUserBadgesResponse,
  OnlineStatusData,
  PplTalkMessage,
} from '../types/pplTalk';

// ─── Callable references ────────────────────────────────────────────────────

const getPublicUserProfileFn = httpsCallable<
  { userId?: string; userHandle?: string },
  GetPublicProfileResponse
>(FIREBASE_FUNCTIONS, 'getPublicUserProfileEurW');

const getUserBadgesFn = httpsCallable<
  { userId: string },
  GetUserBadgesResponse
>(FIREBASE_FUNCTIONS, 'getUserBadgesEurW');

const getUserOnlineStatusFn = httpsCallable<
  { targetUserId: string; staleMinutes?: number },
  OnlineStatusData
>(FIREBASE_FUNCTIONS, 'getUserOnlineStatusEurW');

const logProfileViewFn = httpsCallable<
  { profileUserId: string },
  { success: boolean }
>(FIREBASE_FUNCTIONS, 'logProfileViewEurW');

const getPplTalkMessagesFn = httpsCallable<
  { userId?: string; sortMode?: string; limit?: number; lastMessageId?: string },
  { success: boolean; messages: PplTalkMessage[]; hasMore: boolean; lastMessageId?: string }
>(FIREBASE_FUNCTIONS, 'getPplTalkMessagesEurW');

// ─── Exported service functions ─────────────────────────────────────────────

/**
 * Fetch a public user profile. Tries by userId first, falls back to userHandle.
 */
export async function getPublicProfile(identifier: string): Promise<GetPublicProfileResponse> {
  // Firebase UIDs are typically 28 alphanumeric chars
  const looksLikeUid = /^[a-zA-Z0-9]{20,}$/.test(identifier);

  try {
    const params = looksLikeUid
      ? { userId: identifier }
      : { userHandle: identifier };
    const result = await getPublicUserProfileFn(params);
    // Cloud function returns { success, profile }, unwrap to the profile object
    const data = result.data as any;
    return data.profile ?? data;
  } catch (error) {
    // If userId lookup failed, try as handle
    if (looksLikeUid) {
      try {
        const result = await getPublicUserProfileFn({ userHandle: identifier });
        const data = result.data as any;
        return data.profile ?? data;
      } catch {
        // Both failed
      }
    }
    throw error;
  }
}

/**
 * Fetch badges for a user.
 */
export async function getUserBadges(userId: string): Promise<GetUserBadgesResponse> {
  const result = await getUserBadgesFn({ userId });
  return result.data;
}

/**
 * Check if a user is currently online.
 */
export async function getUserOnlineStatus(targetUserId: string): Promise<OnlineStatusData> {
  const result = await getUserOnlineStatusFn({ targetUserId, staleMinutes: 5 });
  return result.data;
}

/**
 * Log that the current user viewed a profile. Requires auth.
 */
export async function logProfileView(profileUserId: string): Promise<void> {
  try {
    await logProfileViewFn({ profileUserId });
  } catch {
    // Silently fail — not critical
  }
}

/**
 * Fetch a user's pplTalk posts. Used on the profile "Posts" tab.
 */
export async function getUserPosts(
  userId: string,
  limit = 10,
  lastMessageId?: string
): Promise<{ messages: PplTalkMessage[]; hasMore: boolean; lastMessageId?: string }> {
  const result = await getPplTalkMessagesFn({
    userId,
    sortMode: 'new',
    limit,
    lastMessageId,
  });
  return result.data;
}
